import React from "react";
import { FaSearch } from "react-icons/fa";
import { Box, Typography, Button, InputBase, IconButton, Modal, Tooltip } from "@mui/material";
import { styled } from "@mui/system";
import homepageHeader from "../../assets/images/homepageHeader.jpg";
import { GridCloseIcon } from "@mui/x-data-grid";
import AuthContext from "../Context/AuthProvider";

const StyledHeader = styled(Box)(({ theme }) => ({
  backgroundImage: `linear-gradient(150deg, rgba(0, 140, 128, 0.5), rgba(91, 3, 3, 0.5)), url(${homepageHeader})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  height: "50vh",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  color: "#000",
  textAlign: "center",
  padding: "0 20px",
  fontFamily: "'Nunito', sans-serif",
  [theme.breakpoints.down("sm")]: {
    height: "40vh",
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "6px",
  padding: "6px 10px",
  flex: 1,
  border: "none",
  fontSize: "16px",
  outline: "none",
  height: "36px",
  [theme.breakpoints.down("sm")]: {
    padding: "4px 8px",
    fontSize: "14px",
    height: "32px",
  },
}));

const ResponsiveButton = styled(Button)(({ theme }) => ({
  px: 2,
  py: 1.5,
  height: "36px",
  bgcolor: "rgb(8, 65, 240)",
  borderRadius: "6px",
  textTransform: "none",
  fontSize: "18px",
  [theme.breakpoints.down("sm")]: {
    px: 2,
    py: 0.8,
    fontSize: "14px",
    height: "32px",
  },
}));

function Header({ 
  heading, 
  subtext, 
  searchText, 
  setSearchText, 
  invokeApplyFilterWithDelay, 
  renderPreviewModal 
}) {
  const [openModal, setOpenModal] = React.useState(false);
  const { isUserPaymentValid } = React.useContext(AuthContext);
  const inputRef = React.useRef();

  const handleSearchChange = (event) => {
    if (!isUserPaymentValid) {
      renderPreviewModal();
      return;
    }
    const newValue = event?.target?.value;
    setSearchText(newValue);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (!isUserPaymentValid) {
      renderPreviewModal();
      return;
    }
    invokeApplyFilterWithDelay(null, null, 10);
  };
  
  return (
    <StyledHeader>
      <Box mb={3}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: "bold",
            color: "#fff",
            fontSize: { xs: "32px", sm: "48px" }, 
          }}
        >
          {heading}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: "#fff",
            mt: 0,
            fontSize: { xs: "14px", sm: "18px" },
          }}
        >
          {subtext}
        </Typography>
      </Box>
      <Box
        component="form"
        display="flex"
        alignItems="center"
        p={1}
        borderRadius="8px"
        gap={2}
        maxWidth="768px"
        width="100%"
        onSubmit={handleSearchSubmit}
        sx={{ backgroundColor: "#fff", mx: { xs: 1, sm: 0 } }}
      >
        <StyledInputBase
          ref={inputRef}
          placeholder="Start looking for a partner"
          value={searchText}
          onChange={handleSearchChange}
        />
        <Tooltip 
          title={!isUserPaymentValid ? "Unlock search feature with annual subscription" : ""}
          placement="top"
        >
          <span>
            <ResponsiveButton
              variant="contained"
              onClick={handleSearchSubmit}
              aria-label="search"
            >
              <FaSearch style={{ color: "#fff" }} />
            </ResponsiveButton>
          </span>
        </Tooltip>
      </Box>
      {!isUserPaymentValid && (
        <Box mt={2} textAlign="center">
          <Typography
            variant="body1"
            sx={{
              color: "#fff",
              fontWeight: 500,
              fontSize: { xs: "15px", sm: "16px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            Currently in
            <Button
              onClick={renderPreviewModal}
              sx={{
                ml: 1,
                px: 1.5,
                py: 0.2,
                borderRadius: "999px",
                backgroundColor: "#0841f0",
                color: "#fff",
                fontWeight: 600,
                fontSize: { xs: "13px", sm: "14px" },
                textTransform: "none",
                boxShadow: 1,
                cursor: "pointer",
                minWidth: "auto",
                '&:hover': {
                  backgroundColor: '#0636c7',
                  boxShadow: 2,
                },
              }}
              disableElevation
            >
              PREVIEW MODE
            </Button>
          </Typography>
        </Box>
      )}
      {/* Buttons for Disclaimer and Preview */}
      <Box mt={2} display="flex" justifyContent="center" gap={2}>
        <Button
          variant="outlined"
          onClick={() => setOpenModal(true)}
          sx={{
            color: "#fff",
            borderColor: "#fff",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          Show Disclaimer
        </Button>
      </Box>

      {/* Modal for Disclaimer */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "50%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            outline: "none",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography id="modal-title" variant="h6" component="h2">
              Disclaimer
            </Typography>
            <IconButton
              onClick={() => setOpenModal(false)}
              aria-label="close"
              sx={{ color: "#000" }}
            >
              <GridCloseIcon />
            </IconButton>
          </Box>

          <Typography id="modal-description" sx={{ mt: 2 }}>
            We are working hard to keep the platform exclusive to GSB members. 
            If you come across any profiles that seem to belong to non-GSB members, 
            or if you're contacted by any non-GSB member via the platform, 
            please let us know by calling {" "}
            <a
              href="tel:+918296919479"
              style={{ color: "#1976d2", textDecoration: "none", fontWeight: "bold" }}
            >
              +91 82969 19479
            </a>.
          </Typography>

          <Box mt={3} textAlign="center">
            <Button
              variant="contained"
              onClick={() => setOpenModal(false)}
              sx={{ backgroundColor: "#1976d2", color: "#fff" }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </StyledHeader>
  );
}

export default Header;
